import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { FIELD_CLASS } from '@/components/properties/AddressFields';
import { VehicleFormDialog } from '@/components/fleet/VehicleFormDialog';
import { useHasPermission } from '@/auth/RequirePermission';
import { Permissions } from '@/auth/permissions';
import { useVehicleToolSearch, useVehicles } from '@/routes/fleet/useFleet';

/**
 * The account's vehicles, and the equipment on them (M1-27).
 *
 * <b>It is a top-level section rather than something reached from People</b>,
 * unlike M1-38's `/people/skills`. A skill catalog is a vocabulary you go and
 * maintain occasionally, from the roster it describes; a fleet is a set of
 * records with their own detail pages that dispatch and the office look up
 * directly ("which truck has the jetter", "is Van 3 back yet"). It is gated on
 * `users.read` so a Dispatcher sees it — the same permission the API uses, and
 * the reason the board's tooltip can name the truck.
 *
 * <b>The tool search shares this screen rather than getting its own.</b> Its
 * answer IS a vehicle, so it belongs beside the list of them, and a separate
 * route for one search box would be a section of the product that is really a
 * control.
 */
export function Fleet() {
  const { t } = useTranslation();
  // ⚠️ Read and write are two different permissions here (M1-27): a Dispatcher
  // holds `users.read` and reaches this screen — the board's tooltip names the
  // truck — but cannot change the fleet. They get NO affordance rather than a
  // button that renders and then 403s, the same call M1-37, M1-38 and M1-39
  // each made. The API is still the enforcement point; this only decides what
  // to draw.
  const canManage = useHasPermission()(Permissions.manageUsers);
  const vehicles = useVehicles();
  const [adding, setAdding] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [search, setSearch] = useState('');
  const tools = useVehicleToolSearch(search);

  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('fleet.heading')}</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">{t('fleet.description')}</p>
        </div>
        {canManage ? (
          <Button onClick={() => setAdding(true)}>{t('fleet.add')}</Button>
        ) : null}
      </div>

      {vehicles.isPending ? (
        <LoadingState label={t('fleet.loading')} />
      ) : vehicles.isError ? (
        <ErrorState title={t('fleet.loadErrorTitle')} onRetry={() => void vehicles.refetch()} />
      ) : vehicles.data.length === 0 ? (
        <EmptyState title={t('fleet.emptyTitle')} description={t('fleet.emptyDescription')} />
      ) : (
        <ul className="space-y-2" data-testid="fleet-list">
          {vehicles.data.map((vehicle) => (
            <li key={vehicle.id} className="rounded-lg border p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="flex items-start gap-3">
                  {/* ⚠️ `alt=""`, deliberately. The photo is here so somebody
                      can recognise the van on sight; the label naming it sits
                      immediately beside it, so a described image would only
                      announce the same words twice. Decorative is the honest
                      classification, and the detail page — where the photo IS
                      the subject — names it properly instead. */}
                  {vehicle.photoThumbnailUrl ? (
                    <img
                      src={vehicle.photoThumbnailUrl}
                      alt=""
                      className="h-12 w-16 shrink-0 rounded border object-cover"
                      data-testid={`vehicle-thumbnail-${vehicle.id}`}
                    />
                  ) : (
                    <div
                      aria-hidden="true"
                      className="h-12 w-16 shrink-0 rounded border border-dashed"
                    />
                  )}
                  <div>
                    <Link
                      to={`/fleet/${vehicle.id}`}
                      className="font-medium underline underline-offset-2"
                    >
                      {vehicle.label}
                    </Link>
                    <p className="text-sm text-[var(--color-muted-foreground)]">
                      {describeVehicle(vehicle, t)}
                    </p>
                  </div>
                </div>
                {/* Status is a WORD, never a colour alone — M1-09's rule and
                    the palette rule in web/CONVENTIONS.md. The destructive
                    token is the already-verified pairing; nothing new to
                    check. */}
                <span
                  className={
                    vehicle.status === 'Active'
                      ? 'text-xs text-[var(--color-muted-foreground)]'
                      : 'text-xs font-medium text-[var(--color-destructive)]'
                  }
                  data-vehicle-status={vehicle.status}
                >
                  {t(`fleet.status.${vehicle.status}`)}
                </span>
              </div>
              <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                {vehicle.currentDriverName
                  ? t('fleet.drivenBy', { name: vehicle.currentDriverName })
                  : t('fleet.noDriver')}
                {' · '}
                {t('fleet.toolCount', { count: Number(vehicle.toolCount) })}
              </p>
            </li>
          ))}
        </ul>
      )}

      <div className="space-y-3 rounded-lg border p-4">
        <div>
          <h2 className="text-lg font-semibold">{t('fleet.toolSearch.heading')}</h2>
          {/* The AC's own question, quoted at the office rather than
              paraphrased: it is what tells somebody what this box is for. */}
          <p className="text-sm text-[var(--color-muted-foreground)]">
            {t('fleet.toolSearch.description')}
          </p>
        </div>

        {/* A real <form>, as the customer search is: Enter submits on a phone
            keyboard, which is where the "search" key otherwise does nothing.
            Submitting is a no-op beyond that — the hook's debounce has already
            run — so it only prevents the page reload. */}
        <form role="search" onSubmit={(event) => event.preventDefault()}>
          <label htmlFor="fleet-tool-search" className="text-sm font-medium">
            {t('fleet.toolSearch.label')}
          </label>
          <input
            id="fleet-tool-search"
            type="search"
            className={FIELD_CLASS}
            value={search}
            placeholder={t('fleet.toolSearch.placeholder')}
            onChange={(event) => setSearch(event.target.value)}
          />
        </form>

        {tools.isPending ? (
          <LoadingState label={t('fleet.toolSearch.loading')} />
        ) : tools.isError ? (
          <ErrorState
            title={t('fleet.toolSearch.loadErrorTitle')}
            onRetry={() => void tools.refetch()}
          />
        ) : tools.data.length === 0 ? (
          // ⚠️ Two different empty results, said differently. "Nothing matches
          // 'jetter'" and "no equipment recorded at all" would send the office
          // looking in two different places, and collapsing them into one
          // sentence is how somebody concludes the shop has no jetter when
          // really nobody has typed one in yet.
          <p className="text-sm text-[var(--color-muted-foreground)]">
            {search
              ? t('fleet.toolSearch.noMatches', { search })
              : t('fleet.toolSearch.noToolsAtAll')}
          </p>
        ) : (
          <ul className="space-y-1" data-testid="tool-search-results">
            {tools.data.map((tool) => (
              <li key={tool.id} className="text-sm">
                <span className="font-medium">{tool.name}</span>
                {tool.serial ? (
                  <span className="ml-1 text-xs text-[var(--color-muted-foreground)]">
                    {tool.serial}
                  </span>
                ) : null}
                {' — '}
                <Link to={`/fleet/${tool.vehicleId}`} className="underline underline-offset-2">
                  {tool.vehicleLabel}
                </Link>
                {/* A retired van in the back of the yard still HAS the camera
                    rig, so the search does not hide it — but the answer has to
                    say so, or somebody drives out to a truck that is off the
                    road. */}
                {tool.vehicleStatus !== 'Active' ? (
                  <span className="ml-1 text-xs text-[var(--color-destructive)]">
                    {t(`fleet.status.${tool.vehicleStatus}`)}
                  </span>
                ) : null}
                <span className="ml-2 text-xs text-[var(--color-muted-foreground)]">
                  {t(`fleet.condition.${tool.condition}`)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {adding ? (
        <VehicleFormDialog onClose={() => setAdding(false)} onAnnounce={setAnnouncement} />
      ) : null}

      {/* ⚠️ Every comparable screen in this app has one of these and the fleet
          screens shipped without any: eight mutations completing in silence for
          a screen-reader user, with the axe scans green throughout because axe
          cannot check that something was announced. Same shape as
          TechnicianProfileDetail's. (It said "ten" until review counted them:
          vehicle create/edit, assignment create/edit/remove, tool
          create/edit/remove.) */}
      <p role="status" aria-live="polite" className="sr-only" data-testid="fleet-announcement">
        {announcement}
      </p>
    </section>
  );
}

/** Make, model and year as one line, skipping whatever nobody filled in. */
function describeVehicle(
  vehicle: { type: string; make?: string | null; model?: string | null; year?: number | string | null },
  t: (key: string) => string,
): string {
  const parts = [
    t(`fleet.type.${vehicle.type}`),
    [vehicle.year == null ? null : String(vehicle.year), vehicle.make, vehicle.model]
      .filter(Boolean)
      .join(' '),
  ].filter((part) => part !== '');
  return parts.join(' · ');
}
